import React, { useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { TrendingDown, CheckCircle2 } from "lucide-react";
import HeldFundsPicker from "./HeldFundsPicker";

const PERIODS = [
  { key: "return_1yr_pct", label: "1Y" },
  { key: "return_3yr_pct", label: "3Y" },
  { key: "return_5yr_pct", label: "5Y" },
];

export default function UnderperformanceTool({ clientId }) {
  const [selected, setSelected] = useState([]);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [activePeriod, setActivePeriod] = useState("return_3yr_pct");

  const run = async () => {
    if (selected.length < 1) { toast.error("Pick at least 1 fund"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/tools/underperformance", {
        fund_selections: selected,
        time_periods: PERIODS.map((p) => p.key),
      });
      setResult(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Analysis failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <Card className="border-border bg-white p-6">
        <div className="mb-4 flex items-center gap-2">
          <TrendingDown className="h-4 w-4 text-primary" />
          <h3 className="font-dashboard text-base font-bold">Fund selection</h3>
        </div>
        <HeldFundsPicker clientId={clientId} value={selected} onChange={setSelected} showAmount={false} minPicks={1} />
        <Button onClick={run} disabled={busy || selected.length < 1} className="mt-4 w-full md:w-auto" data-testid="run-underperf-btn">
          {busy ? "Analysing…" : `Analyse ${selected.length} fund${selected.length === 1 ? "" : "s"}`}
        </Button>
      </Card>

      {result && (
        <Card className="border-border bg-white p-6" data-testid="underperf-result">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <h4 className="font-dashboard font-bold">Return comparison</h4>
            <div className="flex gap-1 rounded-md border border-border bg-secondary/40 p-1">
              {PERIODS.map((p) => (
                <button
                  key={p.key}
                  onClick={() => setActivePeriod(p.key)}
                  className={`rounded-sm px-3 py-1 text-xs font-medium transition ${
                    activePeriod === p.key ? "bg-white text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
                  }`}
                  data-testid={`period-tab-${p.key}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase text-muted-foreground">
                  <th className="pb-2">Fund</th>
                  <th className="pb-2 text-right">Fund return</th>
                  <th className="pb-2 text-right">Benchmark</th>
                  <th className="pb-2 text-right">Peer avg</th>
                  <th className="pb-2">Flags</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {result.rows.map((r, i) => {
                  const p = r.periods[activePeriod];
                  const belowB = p?.below_benchmark;
                  const belowP = p?.below_peer_avg;
                  return (
                    <tr key={i} data-testid={`underperf-row-${i}`}>
                      <td className="py-3">
                        <div className="font-medium">{r.fund_name}</div>
                        <div className="text-xs text-muted-foreground">{r.sub_category || "—"} · Bench: {r.benchmark_name || "not mapped"}</div>
                      </td>
                      <td className="py-3 text-right tabular-nums font-semibold">{fmt(p?.fund_return_pct)}</td>
                      <td className="py-3 text-right tabular-nums text-muted-foreground">{fmt(p?.benchmark_return_pct)}</td>
                      <td className="py-3 text-right tabular-nums text-muted-foreground">{fmt(p?.peer_avg_return_pct)}</td>
                      <td className="py-3">
                        <div className="flex flex-wrap gap-1">
                          {belowB && <Flag tone="danger" label="Below Benchmark" testId={`flag-below-bench-${i}`} />}
                          {belowP && <Flag tone="warning" label="Below Peers" testId={`flag-below-peers-${i}`} />}
                          {!belowB && !belowP && <span className="inline-flex items-center gap-1 rounded-full bg-secondary px-2 py-0.5 text-[10px] text-muted-foreground"><CheckCircle2 className="h-3 w-3" /> On par</span>}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <p className="mt-4 text-xs text-muted-foreground">Peer average is computed from funds in your own research DB sharing the same sub-category. Benchmark data is entered manually — sync-to-market is a P1 follow-up.</p>
        </Card>
      )}
    </div>
  );
}

const fmt = (v) => (v == null ? "—" : `${v.toFixed(2)}%`);

function Flag({ tone, label, testId }) {
  const cls = tone === "danger"
    ? "bg-destructive/10 text-destructive"
    : "bg-accent/10 text-accent";
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${cls}`} data-testid={testId}>
      {label}
    </span>
  );
}
